import React from "react";

import { Button } from "components/Graphs/Button";
import { Badge } from "components/Graphs/Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";

import { MoreVertical, Edit3, Trash2, Cpu, Zap, Server } from "lucide-react";
import {
  CloudPolicy,
  DefaultRule,
  getDefaultRuleLabel,
} from "components/CloudPolicy/types";
import { FlatCard, Spinner } from "components/UI-lib";
import { CloudIcon } from "@heroicons/react/24/outline";

interface PolicyCardProps {
  policy: CloudPolicy;
  onManage: () => void;
  onDelete: () => void;
  isDeletingPolicy: boolean;
}

export default function PolicyCard({
  policy,
  onManage,
  onDelete,
  isDeletingPolicy,
}: PolicyCardProps) {
  const cardContent = (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2 text-primary">
                <CloudIcon className="h-6 w-6" />
                <h3 className="text-lg leading-tight">
                  {policy.context?.name}
                </h3>
              </div>
              <p className="text-xs">{policy.cloud_cred_id}</p>
            </div>
          </div>
          {isDeletingPolicy ? (
            <div className="flex items-center justify-center w-4 h-4">
              <Spinner type="primary" />
            </div>
          ) : (
            <Button variant="ghost" className="" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>
      <div className={"mt-5"}>
        <div className="flex items-center justify-between mb-3">
          <Badge
            variant={
              policy.default_rule === DefaultRule.DEFAULT_RULE_ALLOW
                ? "default"
                : "error"
            }
            className="capitalize"
          >
            {getDefaultRuleLabel(policy.default_rule)}
          </Badge>
          <span className="text-sm text-secondary">
            {policy.quotas?.length || 0} Quota
            {policy.quotas?.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      <div className="pt-0">
        <Button variant="secondary" className="w-full" onClick={onManage}>
          <Edit3 className="mr-2 h-4 w-4" />
          View and Manage Policy
        </Button>
      </div>
    </>
  );

  return (
    <FlatCard className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow duration-200">
      {cardContent}
    </FlatCard>
  );
}
